import {
  useQueryStates,
  parseAsString,
  parseAsInteger,
  parseAsJson,
} from 'nuqs';
import { useCallback, useMemo } from 'react';
import { STEPS, TOTAL_STEPS } from '../constants';

/**
 * Custom parser that sanitizes string values from URL
 * Converts string "undefined" or "null" to actual null
 * Prevents malicious users from bypassing validation
 */
const parseAsNullableString = {
  parse: (value) => {
    if (!value || value === 'undefined' || value === 'null') {
      return null;
    }
    return value;
  },
  serialize: (value) => {
    return value || '';
  },
};

/**
 * URL parameter parsers for spray calculator state
 */
const urlParsers = {
  // Navigation
  step: parseAsInteger.withDefault(STEPS.SPRAYER),
  view: parseAsString.withDefault('form'),

  // Step 1: Sprayer
  tankVolume: parseAsString.withDefault(''),
  tankVolumeUnitId: parseAsNullableString,

  // Step 2: Area
  totalArea: parseAsString.withDefault(''),
  areaUnitId: parseAsNullableString,
  applicationType: parseAsString.withDefault('full_surface'),
  bandPercentage: parseAsString.withDefault(''),
  rowSpacing: parseAsString.withDefault(''),
  rowSpacingUnitId: parseAsNullableString,
  bandWidth: parseAsString.withDefault(''),
  bandWidthUnitId: parseAsNullableString,

  // Step 3: Treatment
  waterVolume: parseAsString.withDefault(''),
  waterVolumeUnitId: parseAsNullableString,
  products: parseAsJson((value) => {
    if (Array.isArray(value)) return value;
    return null;
  }).withDefault([]),
};

/**
 * Hook for managing URL state with nuqs
 * Pure URL state management without business logic
 *
 * @returns {Object} URL state and setters
 */
export function useURLState() {
  // Use nuqs for URL state management
  const [urlState, setUrlState] = useQueryStates(urlParsers, {
    history: 'replace',
    shallow: false,
    throttleMs: 300,
    clearOnDefault: true,
  });

  // Navigation actions
  const goToStep = useCallback(
    (step) => {
      if (step >= STEPS.SPRAYER && step <= TOTAL_STEPS) {
        setUrlState({ step });
      }
    },
    [setUrlState]
  );

  const goToNextStep = useCallback(() => {
    if (urlState.step < TOTAL_STEPS) {
      setUrlState({ step: urlState.step + 1 });
    }
  }, [urlState.step, setUrlState]);

  const goToPreviousStep = useCallback(() => {
    if (urlState.step > STEPS.SPRAYER) {
      setUrlState({ step: urlState.step - 1 });
    }
  }, [urlState.step, setUrlState]);

  // View mode actions
  const goToResults = useCallback(() => {
    setUrlState({ view: 'results' });
  }, [setUrlState]);

  const goToForm = useCallback(() => {
    setUrlState({ view: 'form' });
  }, [setUrlState]);

  // Build selections object for compatibility
  const selections = useMemo(
    () => ({
      // Step 1
      tankVolume: urlState.tankVolume || '',
      tankVolumeUnitId: urlState.tankVolumeUnitId || null,

      // Step 2
      totalArea: urlState.totalArea || '',
      areaUnitId: urlState.areaUnitId || null,
      applicationType: urlState.applicationType || 'full_surface',
      bandPercentage: urlState.bandPercentage || '',
      rowSpacing: urlState.rowSpacing || '',
      rowSpacingUnitId: urlState.rowSpacingUnitId || null,
      bandWidth: urlState.bandWidth || '',
      bandWidthUnitId: urlState.bandWidthUnitId || null,

      // Step 3
      waterVolume: urlState.waterVolume || '',
      waterVolumeUnitId: urlState.waterVolumeUnitId || null,
      products: urlState.products || [],
    }),
    [urlState]
  );

  // Reset all URL state
  const resetUrlState = useCallback(() => {
    setUrlState({
      step: STEPS.SPRAYER,
      view: 'form',
      tankVolume: '',
      tankVolumeUnitId: null,
      totalArea: '',
      areaUnitId: null,
      applicationType: 'full_surface',
      bandPercentage: '',
      rowSpacing: '',
      rowSpacingUnitId: null,
      bandWidth: '',
      bandWidthUnitId: null,
      waterVolume: '',
      waterVolumeUnitId: null,
      products: [],
    });
  }, [setUrlState]);

  // Helper flags
  const isFirstStep = urlState.step === STEPS.SPRAYER;
  const isLastStep = urlState.step === TOTAL_STEPS;

  // Get shareable URL
  const getShareableURL = useCallback(() => {
    return window.location.href;
  }, []);

  return {
    // Raw URL state
    urlState,
    setUrlState,

    // Derived state
    currentStep: urlState.step,
    currentView: urlState.view,
    selections,

    // Navigation
    goToStep,
    goToNextStep,
    goToPreviousStep,

    // View
    goToResults,
    goToForm,

    // Reset
    resetUrlState,

    // Flags
    isFirstStep,
    isLastStep,

    // Sharing
    getShareableURL,
  };
}

export default useURLState;
